import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Buildings, Bank, User, House, Tray, CheckCircle, Warning, FileArrowDown,
  Plus, Trash, PencilSimple, X, FloppyDisk, MagicWand, Receipt, Scales, PaperPlaneTilt, Copy,
  ChartBar, ArrowsLeftRight,
} from "@phosphor-icons/react";

const GOLD = "#D4A24C";
const RED = "#D90429";

const ENTITY_META = {
  compagnie: { label: "Compagnie", icon: Buildings, color: "#D90429" },
  fondation: { label: "Fondation / OBNL", icon: Bank, color: "#7C3AED" },
  personnel: { label: "Personnel", icon: User, color: "#2563eb" },
  duplex: { label: "Duplex / Locatif", icon: House, color: "#0d9488" },
};
const ENTITY_KEYS = ["compagnie", "fondation", "personnel", "duplex"];
const VALIDATIONS = ["a_classer", "valide", "a_corriger", "doublon", "incomplet"];
const VAL_LABEL = {
  a_classer: "À classer", valide: "Validé", a_corriger: "À corriger",
  doublon: "Doublon", incomplet: "Incomplet",
};
const DEBT_STATUS = {
  a_appeler: "À appeler", en_cours: "En cours", entente: "Entente",
  paye: "Payé", regle: "Réglé",
};

const money = (n) => `${(Number(n) || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;

export default function ComptabiliteKaizenModule() {
  const [tab, setTab] = useState("tableau");
  const [overview, setOverview] = useState(null);

  const loadOverview = useCallback(() => {
    api.get("/kz-compta/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const TABS = [
    { id: "tableau", label: "Tableau du jour", icon: Tray },
    { id: "inbox", label: "Boîte d'entrée", icon: Receipt },
    { id: "entites", label: "Par entité", icon: Buildings },
    { id: "vues", label: "Assurances · Intérêts · Revenus", icon: Scales },
    { id: "rapports", label: "Rapports fiscaux & Taxes", icon: ChartBar },
    { id: "banque", label: "Rapprochement bancaire", icon: ArrowsLeftRight },
    { id: "doublons", label: "Doublons & pièces", icon: Copy },
    { id: "dettes", label: "Dettes gouvernement", icon: Warning },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="kz-compta-module">
      <div className="border-b border-zinc-800 bg-gradient-to-r from-zinc-950 to-zinc-900 px-6 py-6">
        <h1 className="text-2xl font-black tracking-tight" style={{ color: GOLD }}>KAIZEN COMPTABILITÉ</h1>
        <p className="text-sm text-zinc-400 mt-1">Classement par entité · validation · export comptable & gouvernement</p>
      </div>

      <div className="flex flex-wrap gap-2 px-6 py-4 border-b border-zinc-800">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`kz-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.id ? "bg-white text-zinc-950" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {tab === "tableau" && <TableauDuJour overview={overview} reload={loadOverview} goto={setTab} />}
        {tab === "inbox" && <BoiteEntree reload={loadOverview} />}
        {tab === "entites" && <ParEntite overview={overview} />}
        {tab === "vues" && <VuesSpeciales />}
        {tab === "rapports" && <RapportsFiscaux />}
        {tab === "banque" && <RapprochementBancaire />}
        {tab === "doublons" && <DoublonsPieces reload={loadOverview} />}
        {tab === "dettes" && <Dettes reload={loadOverview} />}
      </div>
    </div>
  );
}

/* ---------------- Tableau du jour ---------------- */
function TableauDuJour({ overview, reload, goto }) {
  const [busy, setBusy] = useState(false);
  const [showAcct, setShowAcct] = useState(false);
  if (!overview) return <p className="text-zinc-500">Chargement…</p>;

  const autoClassify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/kz-compta/auto-classify");
      toast.success(`${r.data.applied} reçus pré-classés automatiquement par entité.`);
      reload();
    } catch { toast.error("Échec du classement automatique."); }
    setBusy(false);
  };

  const stats = [
    { label: "Documents", value: overview.total_documents, color: "#fff", test: "stat-total" },
    { label: "À classer (sans entité)", value: overview.a_classer, color: GOLD, test: "stat-aclasser" },
    { label: "À valider", value: overview.a_valider ?? 0, color: "#f59e0b", test: "stat-avalider" },
    { label: "À envoyer au comptable", value: overview.a_envoyer_comptable, color: "#16a34a", test: "stat-aenvoyer" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} data-testid={`kz-${s.test}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <div className="text-3xl font-black" style={{ color: s.color }}>{s.value}</div>
            <div className="text-sm text-zinc-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <button data-testid="kz-auto-classify-btn" onClick={autoClassify} disabled={busy}
          className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-zinc-950 disabled:opacity-50"
          style={{ background: GOLD }}>
          <MagicWand size={18} /> {busy ? "Classement…" : "Classer automatiquement (depuis la répartition)"}
        </button>
        <button data-testid="kz-goto-inbox" onClick={() => goto("inbox")}
          className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold bg-zinc-800 hover:bg-zinc-700">
          <Receipt size={18} /> Ouvrir la boîte d'entrée
        </button>
        <button data-testid="kz-send-accountant-btn" onClick={() => setShowAcct(true)}
          className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold bg-green-700 hover:bg-green-600">
          <PaperPlaneTilt size={18} /> Envoyer le dossier au comptable
        </button>
      </div>

      {showAcct && <SendAccountantModal onClose={() => setShowAcct(false)} />}

      <div>
        <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wide mb-3">Répartition par entité</h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {ENTITY_KEYS.map((k) => {
            const m = ENTITY_META[k];
            const d = overview.by_entity[k] || { count: 0, total: 0 };
            return (
              <div key={k} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" style={{ borderTopColor: m.color, borderTopWidth: 3 }}>
                <m.icon size={22} style={{ color: m.color }} />
                <div className="text-xl font-black mt-2">{money(d.total)}</div>
                <div className="text-xs text-zinc-400">{m.label} · {d.count} doc.</div>
              </div>
            );
          })}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <Tray size={22} className="text-zinc-500" />
            <div className="text-xl font-black mt-2">{money(overview.by_entity.non_classe?.total)}</div>
            <div className="text-xs text-zinc-400">Non classé · {overview.by_entity.non_classe?.count || 0} doc.</div>
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 flex items-center justify-between">
        <div>
          <div className="text-sm text-zinc-400">Dettes gouvernement actives</div>
          <div className="text-2xl font-black" style={{ color: RED }}>{money(overview.debts_total)} <span className="text-sm text-zinc-500">({overview.debts_count} dossiers)</span></div>
        </div>
        <button data-testid="kz-goto-dettes" onClick={() => goto("dettes")} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold">Gérer les dettes</button>
      </div>
    </div>
  );
}

/* ---------------- Boîte d'entrée ---------------- */
function BoiteEntree({ reload }) {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [bulkEntity, setBulkEntity] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/kz-compta/receipts?inbox=true&limit=400")
      .then((r) => setRows(r.data.receipts)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setOne = async (id, patch) => {
    try {
      await api.patch(`/kz-compta/receipts/${id}`, patch);
      setRows((prev) => prev.filter((r) => r.id !== id || (patch.kz_validation !== "valide" && !patch.kz_entity)));
      toast.success("Reçu classé.");
      reload();
    } catch { toast.error("Erreur."); }
  };

  const applyBulk = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length || !bulkEntity) { toast.error("Sélectionne des reçus + une entité."); return; }
    try {
      const r = await api.post("/kz-compta/receipts/bulk-classify", { receipt_ids: ids, kz_entity: bulkEntity, kz_validation: "valide" });
      toast.success(`${r.data.modified} reçus classés en « ${ENTITY_META[bulkEntity].label} ».`);
      setSel({}); setBulkEntity(""); load(); reload();
    } catch { toast.error("Erreur du classement groupé."); }
  };

  const selCount = Object.values(sel).filter(Boolean).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <span className="text-sm text-zinc-400">{selCount} sélectionné(s)</span>
        <select data-testid="kz-bulk-entity" value={bulkEntity} onChange={(e) => setBulkEntity(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm">
          <option value="">Choisir une entité…</option>
          {ENTITY_KEYS.map((k) => <option key={k} value={k}>{ENTITY_META[k].label}</option>)}
        </select>
        <button data-testid="kz-bulk-apply" onClick={applyBulk}
          className="px-4 py-2 rounded-lg font-semibold text-zinc-950" style={{ background: GOLD }}>
          Classer la sélection
        </button>
        <a href={`${api.defaults.baseURL}/kz-compta/export.csv`} target="_blank" rel="noreferrer"
          data-testid="kz-export-all" className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">
          <FileArrowDown size={16} /> Export CSV (tout)
        </a>
      </div>

      {loading ? <p className="text-zinc-500">Chargement…</p> : rows.length === 0 ? (
        <div className="text-center py-12 text-zinc-500"><CheckCircle size={42} className="mx-auto mb-2 text-green-500" />Boîte d'entrée vide — tout est classé ! 🎉</div>
      ) : (
        <div className="overflow-x-auto border border-zinc-800 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-400">
              <tr>
                <th className="p-3 w-8"></th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Fournisseur</th>
                <th className="p-3 text-left">Catégorie</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-left">Entité</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-zinc-800 hover:bg-zinc-900/50" data-testid={`kz-row-${r.id}`}>
                  <td className="p-3"><input type="checkbox" checked={!!sel[r.id]} onChange={(e) => setSel({ ...sel, [r.id]: e.target.checked })} /></td>
                  <td className="p-3 text-zinc-400">{r.receipt_date || "—"}</td>
                  <td className="p-3 font-medium">{r.merchant || "—"}</td>
                  <td className="p-3 text-zinc-400">{r.category || "—"}</td>
                  <td className="p-3 text-right font-semibold">{money(r.total_amount)}</td>
                  <td className="p-3">
                    <select data-testid={`kz-entity-${r.id}`} value={r.kz_entity || ""}
                      onChange={(e) => e.target.value && setOne(r.id, { kz_entity: e.target.value, kz_validation: "valide" })}
                      className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-xs">
                      <option value="">{r.kz_suggested_entity ? `Suggéré : ${ENTITY_META[r.kz_suggested_entity].label}` : "Classer…"}</option>
                      {ENTITY_KEYS.map((k) => <option key={k} value={k}>{ENTITY_META[k].label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Par entité ---------------- */
function ParEntite({ overview }) {
  const [active, setActive] = useState("compagnie");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/kz-compta/by-entity/${active}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [active]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {ENTITY_KEYS.map((k) => {
          const m = ENTITY_META[k];
          return (
            <button key={k} data-testid={`kz-entity-tab-${k}`} onClick={() => setActive(k)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${active === k ? "text-zinc-950" : "bg-zinc-900 text-zinc-300"}`}
              style={active === k ? { background: m.color } : {}}>
              <m.icon size={16} /> {m.label}
            </button>
          );
        })}
        <a href={`${api.defaults.baseURL}/kz-compta/export.csv?entity=${active}`} target="_blank" rel="noreferrer"
          data-testid="kz-export-entity" className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">
          <FileArrowDown size={16} /> Export « prêt comptable » ({ENTITY_META[active].label})
        </a>
      </div>

      {!detail ? <p className="text-zinc-500">Chargement…</p> : (
        <div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-4">
            <div className="text-sm text-zinc-400">{detail.label}</div>
            <div className="text-3xl font-black" style={{ color: ENTITY_META[active].color }}>{money(detail.total)}</div>
            <div className="text-xs text-zinc-500">{detail.count} documents</div>
          </div>
          <div className="border border-zinc-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400"><tr><th className="p-3 text-left">Catégorie</th><th className="p-3 text-right">Documents</th><th className="p-3 text-right">Total</th></tr></thead>
              <tbody>
                {Object.entries(detail.by_category).map(([cat, v]) => (
                  <tr key={cat} className="border-t border-zinc-800"><td className="p-3">{cat}</td><td className="p-3 text-right text-zinc-400">{v.count}</td><td className="p-3 text-right font-semibold">{money(v.total)}</td></tr>
                ))}
                {Object.keys(detail.by_category).length === 0 && <tr><td colSpan={3} className="p-6 text-center text-zinc-500">Aucun document classé dans cette entité.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Vues spécialisées ---------------- */
function VuesSpeciales() {
  const [view, setView] = useState("assurances");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/kz-compta/views/${view}`).then((r) => setData(r.data)).catch(() => {});
  }, [view]);
  const VIEWS = [{ id: "assurances", l: "Assurances" }, { id: "interets", l: "Intérêts" }, { id: "revenus", l: "Revenus" }];

  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        {VIEWS.map((v) => (
          <button key={v.id} data-testid={`kz-view-${v.id}`} onClick={() => setView(v.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${view === v.id ? "bg-white text-zinc-950" : "bg-zinc-900 text-zinc-300"}`}>{v.l}</button>
        ))}
      </div>
      {!data ? <p className="text-zinc-500">Chargement…</p> : (
        <>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <div className="text-sm text-zinc-400 capitalize">{data.view} — total</div>
            <div className="text-3xl font-black" style={{ color: GOLD }}>{money(data.total)}</div>
            <div className="text-xs text-zinc-500">{data.count} documents</div>
          </div>
          <div className="border border-zinc-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Fournisseur</th><th className="p-3 text-left">Entité</th><th className="p-3 text-right">Total</th></tr></thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-800"><td className="p-3 text-zinc-400">{r.receipt_date || "—"}</td><td className="p-3">{r.merchant || "—"}</td><td className="p-3 text-zinc-400">{r.kz_entity ? ENTITY_META[r.kz_entity]?.label : "Non classé"}</td><td className="p-3 text-right font-semibold">{money(r.total_amount)}</td></tr>
                ))}
                {data.items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-zinc-500">Aucun document dans cette vue.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Dettes gouvernement ---------------- */
const EMPTY_DEBT = { organisme: "", entity: "compagnie", debt_type: "", period: "", amount_due: 0, interest_penalty: 0, due_date: "", last_notice_date: "", contact: "", reference_no: "", desired_arrangement: "", status: "a_appeler", reminders_enabled: true, reminder_days_before: 7, reminder_email: "", notes: "" };

function Dettes({ reload }) {
  const [debts, setDebts] = useState([]);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/kz-compta/debts").then((r) => { setDebts(r.data.debts); setTotal(r.data.total_actif); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.organisme) { toast.error("Organisme requis."); return; }
    try {
      if (editing.id) await api.put(`/kz-compta/debts/${editing.id}`, editing);
      else await api.post("/kz-compta/debts", editing);
      toast.success("Dette enregistrée.");
      setEditing(null); load(); reload();
    } catch { toast.error("Erreur."); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette dette ?")) return;
    await api.delete(`/kz-compta/debts/${id}`); toast.success("Supprimée."); load(); reload();
  };
  const sendReminder = async (id) => {
    try {
      await api.post(`/kz-compta/debts/${id}/send-reminder`);
      toast.success("Rappel envoyé par courriel.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi du rappel."); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-5 py-3">
          <div className="text-xs text-zinc-400">Total dettes actives</div>
          <div className="text-2xl font-black" style={{ color: RED }}>{money(total)}</div>
        </div>
        <button data-testid="kz-debt-add" onClick={() => setEditing({ ...EMPTY_DEBT })}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-zinc-950" style={{ background: GOLD }}>
          <Plus size={16} /> Nouvelle dette
        </button>
        <a href={`${api.defaults.baseURL}/kz-compta/debts/export.csv`} target="_blank" rel="noreferrer"
          data-testid="kz-debt-export" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">
          <FileArrowDown size={16} /> Export « prêt gouvernement »
        </a>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {debts.map((d) => (
          <div key={d.id} data-testid={`kz-debt-${d.id}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-bold">{d.organisme}</div>
                <div className="text-xs text-zinc-400">{d.debt_type || "—"} · {d.period || "—"} · {ENTITY_META[d.entity]?.label || d.entity}</div>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-zinc-800">{DEBT_STATUS[d.status] || d.status}</span>
            </div>
            <div className="text-xl font-black mt-2" style={{ color: RED }}>{money((d.amount_due || 0) + (d.interest_penalty || 0))}</div>
            {d.due_date && <div className="text-xs text-zinc-500">Échéance : {d.due_date}{d.reminders_enabled !== false && <span className="text-amber-400"> · 🔔 rappel J-{d.reminder_days_before || 7}</span>}</div>}
            {d.desired_arrangement && <div className="text-xs text-zinc-400 mt-1">Arrangement : {d.desired_arrangement}</div>}
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => setEditing(d)} className="flex items-center gap-1 text-xs px-3 py-1 rounded bg-zinc-800 hover:bg-zinc-700"><PencilSimple size={14} /> Modifier</button>
              <button data-testid={`kz-debt-remind-${d.id}`} onClick={() => sendReminder(d.id)} className="flex items-center gap-1 text-xs px-3 py-1 rounded bg-zinc-800 hover:bg-amber-900"><PaperPlaneTilt size={14} /> Rappel</button>
              <button onClick={() => del(d.id)} className="flex items-center gap-1 text-xs px-3 py-1 rounded bg-zinc-800 hover:bg-red-900"><Trash size={14} /> Supprimer</button>
            </div>
          </div>
        ))}
        {debts.length === 0 && <p className="text-zinc-500">Aucune dette enregistrée. Ajoute Revenu Québec, ARC, Ville, assureur…</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="kz-debt-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editing.id ? "Modifier" : "Nouvelle"} dette</h3>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Organisme *" span><input data-testid="kz-debt-organisme" value={editing.organisme} onChange={(e) => setEditing({ ...editing, organisme: e.target.value })} className="kz-inp" placeholder="Revenu Québec, ARC, Ville…" /></Field>
              <Field label="Entité"><select value={editing.entity} onChange={(e) => setEditing({ ...editing, entity: e.target.value })} className="kz-inp">{ENTITY_KEYS.map((k) => <option key={k} value={k}>{ENTITY_META[k].label}</option>)}</select></Field>
              <Field label="Type"><input value={editing.debt_type || ""} onChange={(e) => setEditing({ ...editing, debt_type: e.target.value })} className="kz-inp" placeholder="TPS/TVQ, impôt…" /></Field>
              <Field label="Période"><input value={editing.period || ""} onChange={(e) => setEditing({ ...editing, period: e.target.value })} className="kz-inp" placeholder="2024" /></Field>
              <Field label="Montant dû"><input type="number" value={editing.amount_due} onChange={(e) => setEditing({ ...editing, amount_due: parseFloat(e.target.value) || 0 })} className="kz-inp" /></Field>
              <Field label="Intérêts/pénalités"><input type="number" value={editing.interest_penalty} onChange={(e) => setEditing({ ...editing, interest_penalty: parseFloat(e.target.value) || 0 })} className="kz-inp" /></Field>
              <Field label="Échéance"><input type="date" value={editing.due_date || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} className="kz-inp" /></Field>
              <Field label="Dernier avis"><input type="date" value={editing.last_notice_date || ""} onChange={(e) => setEditing({ ...editing, last_notice_date: e.target.value })} className="kz-inp" /></Field>
              <Field label="Contact / service"><input value={editing.contact || ""} onChange={(e) => setEditing({ ...editing, contact: e.target.value })} className="kz-inp" /></Field>
              <Field label="N° référence"><input value={editing.reference_no || ""} onChange={(e) => setEditing({ ...editing, reference_no: e.target.value })} className="kz-inp" /></Field>
              <Field label="Statut" span><select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="kz-inp">{Object.entries(DEBT_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
              <Field label="Rappel (jours avant)"><input type="number" value={editing.reminder_days_before ?? 7} onChange={(e) => setEditing({ ...editing, reminder_days_before: parseInt(e.target.value) || 0 })} className="kz-inp" /></Field>
              <Field label="Courriel du rappel"><input value={editing.reminder_email || ""} onChange={(e) => setEditing({ ...editing, reminder_email: e.target.value })} className="kz-inp" placeholder="(défaut : ton courriel)" /></Field>
              <Field label="Rappels automatiques" span>
                <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={editing.reminders_enabled !== false} onChange={(e) => setEditing({ ...editing, reminders_enabled: e.target.checked })} /> Activer les rappels courriel avant l'échéance</label>
              </Field>
              <Field label="Arrangement souhaité" span><input value={editing.desired_arrangement || ""} onChange={(e) => setEditing({ ...editing, desired_arrangement: e.target.value })} className="kz-inp" placeholder="Paiements mensuels…" /></Field>
              <Field label="Notes" span><textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="kz-inp" rows={2} /></Field>
            </div>
            <button data-testid="kz-debt-save" onClick={save} className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-zinc-950" style={{ background: GOLD }}><FloppyDisk size={18} /> Enregistrer</button>
          </div>
        </div>
      )}
      <style>{`.kz-inp{width:100%;background:#27272a;border:1px solid #3f3f46;border-radius:8px;padding:8px 10px;font-size:13px;color:#fff}`}</style>
    </div>
  );
}

/* ---------------- Doublons & pièces manquantes ---------------- */
function DoublonsPieces({ reload }) {
  const [data, setData] = useState(null);
  const [sub, setSub] = useState("doublons");
  const [cleaning, setCleaning] = useState(false);

  const load = useCallback(() => {
    api.get("/kz-compta/duplicates-missing").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const autoClean = async () => {
    if (!window.confirm("Nettoyer automatiquement tous les doublons ?\n\nL'app garde 1 reçu par groupe (priorité : déjà dans QuickBooks > avec pièce justificative > le plus ancien) et archive les autres. Ils seront exclus des rapports ET du push QuickBooks. C'est réversible.")) return;
    setCleaning(true);
    try {
      const r = await api.post("/kz-compta/duplicates/auto-clean");
      toast.success(r.data.message);
      load(); reload();
    } catch { toast.error("Échec du nettoyage."); }
    setCleaning(false);
  };

  const markDoublon = async (id) => {
    try { await api.patch(`/kz-compta/receipts/${id}`, { kz_validation: "doublon" }); toast.success("Marqué comme doublon."); load(); reload(); }
    catch { toast.error("Erreur."); }
  };
  const markIncomplet = async (id) => {
    try { await api.patch(`/kz-compta/receipts/${id}`, { kz_validation: "incomplet" }); toast.success("Marqué « incomplet »."); load(); reload(); }
    catch { toast.error("Erreur."); }
  };

  if (!data) return <p className="text-zinc-500">Analyse en cours…</p>;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="kz-dup-groups">
          <div className="text-2xl font-black text-amber-400">{data.duplicate_groups}</div>
          <div className="text-xs text-zinc-400">Groupes de doublons ({data.duplicate_extra_count} pièces en trop)</div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="kz-missing-count">
          <div className="text-2xl font-black" style={{ color: RED }}>{data.missing_proof_count}</div>
          <div className="text-xs text-zinc-400">Dépenses sans pièce</div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="text-2xl font-black text-zinc-200">{money(data.missing_proof_total)}</div>
          <div className="text-xs text-zinc-400">Montant sans justificatif</div>
        </div>
      </div>

      {data.duplicate_groups > 0 && (
        <div className="flex items-center justify-between gap-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="kz-autoclean-banner">
          <div className="text-sm text-amber-200">
            <span className="font-bold">{data.duplicate_extra_count} pièces en double</span> détectées sur {data.duplicate_groups} groupe(s).
            Nettoie-les avant de pousser vers QuickBooks pour ne pas polluer tes rapports de taxes.
          </div>
          <button data-testid="kz-autoclean-btn" onClick={autoClean} disabled={cleaning}
            className="shrink-0 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-sm disabled:opacity-50">
            {cleaning ? "Nettoyage…" : "🧹 Nettoyer tous les doublons (1 clic)"}
          </button>
        </div>
      )}

      <div className="flex gap-2">
        <button data-testid="kz-sub-doublons" onClick={() => setSub("doublons")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${sub === "doublons" ? "bg-white text-zinc-950" : "bg-zinc-900 text-zinc-300"}`}>🔁 Doublons</button>
        <button data-testid="kz-sub-missing" onClick={() => setSub("missing")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${sub === "missing" ? "bg-white text-zinc-950" : "bg-zinc-900 text-zinc-300"}`}>📎 Sans pièce</button>
      </div>

      {sub === "doublons" && (
        <div className="space-y-3">
          {data.duplicates.length === 0 && <p className="text-zinc-500">Aucun doublon détecté 🎉</p>}
          {data.duplicates.map((g, idx) => (
            <div key={idx} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`kz-dup-${idx}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold">{g.merchant} <span className="text-zinc-500 text-xs">· {g.receipt_date || "—"} · {money(g.total_amount)}</span></div>
                <span className="text-xs px-2 py-1 rounded-full bg-amber-900/50 text-amber-300">{g.count}× identiques</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {g.receipts.map((r, i) => (
                  <div key={r.id} className="flex items-center gap-2 text-xs bg-zinc-950 border border-zinc-800 rounded px-2 py-1">
                    <span className="text-zinc-400">#{i + 1}</span>
                    {r.kz_validation === "doublon" ? <span className="text-amber-400">déjà marqué doublon</span> :
                      <button onClick={() => markDoublon(r.id)} className="text-amber-300 hover:underline">marquer doublon</button>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {sub === "missing" && (
        <div className="border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-400"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Fournisseur</th><th className="p-3 text-left">Catégorie</th><th className="p-3 text-right">Total</th><th className="p-3"></th></tr></thead>
            <tbody>
              {data.missing_proof.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-zinc-500">Toutes les dépenses ont une pièce 🎉</td></tr>}
              {data.missing_proof.map((r) => (
                <tr key={r.id} className="border-t border-zinc-800" data-testid={`kz-missing-${r.id}`}>
                  <td className="p-3 text-zinc-400">{r.receipt_date || "—"}</td>
                  <td className="p-3 font-medium">{r.merchant || "—"}</td>
                  <td className="p-3 text-zinc-400">{r.category || "—"}</td>
                  <td className="p-3 text-right font-semibold">{money(r.total_amount)}</td>
                  <td className="p-3 text-right"><button onClick={() => markIncomplet(r.id)} className="text-xs text-amber-300 hover:underline">marquer incomplet</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, span, children }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="block text-xs text-zinc-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

/* ---------------- Modale : envoyer au comptable ---------------- */
function SendAccountantModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("Voici mon dossier pour la préparation de fin d'année. Mes questions :\n- ");
  const [ents, setEnts] = useState({ compagnie: true, fondation: true, personnel: true, duplex: true });
  const [includeDebts, setIncludeDebts] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email || !email.includes("@")) { toast.error("Courriel du comptable requis."); return; }
    const entities = ENTITY_KEYS.filter((k) => ents[k]);
    if (!entities.length) { toast.error("Choisis au moins une entité."); return; }
    setBusy(true);
    try {
      const r = await api.post("/kz-compta/send-accountant", {
        accountant_email: email, message, entities, include_debts: includeDebts,
      });
      toast.success(`Brouillon créé dans Gmail (${r.data.attachments} pièces jointes) — relis et envoie.`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création du brouillon.");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="kz-acct-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Envoyer le dossier au comptable</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <p className="text-xs text-zinc-400 mb-4">Crée un <b>brouillon Gmail</b> (non envoyé) avec un CSV par entité + tes questions. Tu relis et tu envoies toi-même.</p>
        <Field label="Courriel du comptable *" span>
          <input data-testid="kz-acct-email" value={email} onChange={(e) => setEmail(e.target.value)} className="kz-inp" placeholder="comptable@exemple.com" />
        </Field>
        <div className="mt-3">
          <label className="block text-xs text-zinc-400 mb-1">Entités à inclure</label>
          <div className="flex flex-wrap gap-2">
            {ENTITY_KEYS.map((k) => (
              <button key={k} type="button" onClick={() => setEnts({ ...ents, [k]: !ents[k] })}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${ents[k] ? "text-zinc-950" : "bg-zinc-800 text-zinc-400"}`}
                style={ents[k] ? { background: ENTITY_META[k].color } : {}}>
                {ENTITY_META[k].label}
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm text-zinc-300">
          <input type="checkbox" checked={includeDebts} onChange={(e) => setIncludeDebts(e.target.checked)} />
          Joindre les dettes gouvernement
        </label>
        <Field label="Message / questions" span>
          <textarea data-testid="kz-acct-message" value={message} onChange={(e) => setMessage(e.target.value)} className="kz-inp mt-1" rows={4} />
        </Field>
        <button data-testid="kz-acct-submit" onClick={submit} disabled={busy}
          className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-zinc-950 disabled:opacity-50" style={{ background: GOLD }}>
          <PaperPlaneTilt size={18} /> {busy ? "Création…" : "Créer le brouillon Gmail"}
        </button>
        <style>{`.kz-inp{width:100%;background:#27272a;border:1px solid #3f3f46;border-radius:8px;padding:8px 10px;font-size:13px;color:#fff}`}</style>
      </div>
    </div>
  );
}


/* ---------------- Rapports fiscaux & Taxes ---------------- */
function RapportsFiscaux() {
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(nowY);
  const [report, setReport] = useState(null);
  const [taxes, setTaxes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState(null);
  const [salesTaxIncl, setSalesTaxIncl] = useState(true);

  const loadSales = useCallback(() => {
    api.get(`/kz-compta/sales-report?year=${year}&tax_included=${salesTaxIncl}`)
      .then((r) => setSales(r.data)).catch(() => {});
  }, [year, salesTaxIncl]);
  useEffect(() => { loadSales(); }, [loadSales]);

  const downloadSalesCsv = async () => {
    try {
      const r = await api.get(`/kz-compta/sales-report.csv?year=${year}&tax_included=${salesTaxIncl}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `rapport_ventes_${year}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Rapport de ventes exporté.");
    } catch { toast.error("Export impossible"); }
  };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/kz-compta/fiscal-report?year=${year}`),
      api.get(`/kz-compta/taxes-report?year=${year}`),
    ]).then(([r, t]) => { setReport(r.data); setTaxes(t.data); })
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const r = await api.get(`/kz-compta/fiscal-report.csv?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `rapport_fiscal_${year}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };

  const downloadCleanCsv = async () => {
    try {
      const r = await api.get(`/kz-compta/export/clean?start=${year}-01-01&end=${year}-12-31`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `export_comptable_propre_${year}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export propre généré (doublons exclus) — prêt à envoyer à David.");
    } catch { toast.error("Export impossible"); }
  };

  const [emailing, setEmailing] = useState(false);
  const emailToAccountant = async () => {
    if (!window.confirm(`Envoyer l'export propre ${year} (doublons exclus) par courriel à david.dumais@mlp.ca ?`)) return;
    setEmailing(true);
    try {
      const r = await api.post("/kz-compta/export/email", { start: `${year}-01-01`, end: `${year}-12-31` });
      toast.success(`Envoyé à ${r.data.to} — ${r.data.count} reçus (TPS ${r.data.summary.tps} $ · TVQ ${r.data.summary.tvq} $).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi."); }
    setEmailing(false);
  };

  const years = [nowY, nowY - 1, nowY - 2, nowY - 3];
  const g = report?.grand_total;

  return (
    <div className="space-y-6" data-testid="kz-rapports">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-zinc-400">Année fiscale :</span>
        {years.map((y) => (
          <button key={y} data-testid={`kz-year-${y}`} onClick={() => setYear(y)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${year === y ? "bg-white text-zinc-950" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>{y}</button>
        ))}
        <button data-testid="kz-fiscal-csv" onClick={downloadCsv}
          className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-zinc-950" style={{ background: GOLD }}>
          <FileArrowDown size={16} /> Export comptable CSV
        </button>
        <button data-testid="kz-clean-csv" onClick={downloadCleanCsv}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-zinc-950">
          <FileArrowDown size={16} /> Export propre pour David (doublons exclus)
        </button>
        <button data-testid="kz-email-david" onClick={emailToAccountant} disabled={emailing}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-sky-500 hover:bg-sky-400 text-zinc-950 disabled:opacity-50">
          <PaperPlaneTilt size={16} /> {emailing ? "Envoi…" : "Envoyer à ma comptable"}
        </button>
      </div>
      <p className="text-xs text-zinc-500 -mt-3">L'« export propre » exclut automatiquement les doublons archivés et inclut un sommaire TPS/TVQ par catégorie — prêt à envoyer à ta comptable / QuickBooks. « Envoyer à ma comptable » l'expédie en 1 clic à david.dumais@mlp.ca (Gmail connecté requis).</p>

      {loading ? <div className="text-zinc-500 text-sm">Chargement…</div> : (
        <>
          {/* Bandeau totaux + taxes récupérables */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card label="Total dépenses" value={money(g?.total)} color="#fff" />
            <Card label="Sous-total (avant taxes)" value={money(g?.subtotal)} color="#a1a1aa" />
            <Card label="TPS payée (récupérable)" value={money(taxes?.total_tps)} color="#22c55e" test="kz-tps" />
            <Card label="TVQ payée (récupérable)" value={money(taxes?.total_tvq)} color="#22c55e" test="kz-tvq" />
          </div>

          {/* Taxes par trimestre */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <h3 className="text-sm font-bold text-zinc-200 mb-3 flex items-center gap-2"><Scales size={16} /> Taxes par trimestre {year}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((q) => {
                const d = taxes?.by_quarter?.[q] || {};
                return (
                  <div key={q} className="rounded-lg bg-zinc-950/60 border border-zinc-800 p-3" data-testid={`kz-q-${q}`}>
                    <div className="text-xs text-zinc-500">T{q} · {d.count || 0} reçus</div>
                    <div className="text-sm text-green-300 mt-1">TPS {money(d.tps)}</div>
                    <div className="text-sm text-green-300">TVQ {money(d.tvq)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rapport de VENTES mensuel (revenus + TPS/TVQ) */}
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="kz-sales-report">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h3 className="text-sm font-bold text-amber-200 flex items-center gap-2"><Scales size={16} /> Rapport de ventes mensuel {year} (revenus encaissés)</h3>
              <div className="flex items-center gap-2">
                <label className="text-xs text-zinc-400 flex items-center gap-1">
                  <input type="checkbox" checked={salesTaxIncl} onChange={(e) => setSalesTaxIncl(e.target.checked)} data-testid="sales-tax-incl" /> Taxes incluses
                </label>
                <button onClick={downloadSalesCsv} data-testid="kz-sales-csv" className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-zinc-950"><FileArrowDown size={14} /> Export CSV</button>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mb-2">{sales?.note} Source : paiements membres encaissés.</p>
            {sales?.remise_gouvernement && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3" data-testid="kz-remise">
                <div className="rounded-lg bg-zinc-950/60 border border-zinc-800 p-2.5">
                  <div className="text-[10px] text-zinc-500">TPS perçue − payée</div>
                  <div className="text-sm text-zinc-200">{money(sales.remise_gouvernement.tps_percue)} − {money(sales.remise_gouvernement.tps_payee_depenses)}</div>
                  <div className="text-sm font-bold text-amber-300">= {money(sales.remise_gouvernement.net_tps_a_remettre)}</div>
                </div>
                <div className="rounded-lg bg-zinc-950/60 border border-zinc-800 p-2.5">
                  <div className="text-[10px] text-zinc-500">TVQ perçue − payée</div>
                  <div className="text-sm text-zinc-200">{money(sales.remise_gouvernement.tvq_percue)} − {money(sales.remise_gouvernement.tvq_payee_depenses)}</div>
                  <div className="text-sm font-bold text-amber-300">= {money(sales.remise_gouvernement.net_tvq_a_remettre)}</div>
                </div>
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/40 p-2.5">
                  <div className="text-[10px] text-amber-200/80">{sales.remise_gouvernement.net_total_a_remettre >= 0 ? "NET À REMETTRE AU GOUVERNEMENT" : "CRÉDIT DE TAXES À RÉCUPÉRER"}</div>
                  <div className="text-lg font-black text-amber-300" data-testid="kz-net-remise">{money(Math.abs(sales.remise_gouvernement.net_total_a_remettre))}</div>
                </div>
              </div>
            )}
            <table className="w-full text-xs">
              <thead><tr className="text-zinc-500 text-left">
                <th className="py-1">Mois</th><th>Nb ventes</th><th>Sous-total</th><th>TPS (5%)</th><th>TVQ (9,975%)</th><th className="text-right">Total encaissé</th>
              </tr></thead>
              <tbody>
                {(sales?.months || []).filter((m) => m.count > 0).length === 0 ? (
                  <tr><td colSpan={6} className="py-3 text-zinc-500" data-testid="kz-sales-empty">Aucune vente encaissée pour {year}.</td></tr>
                ) : (sales?.months || []).map((m) => m.count > 0 && (
                  <tr key={m.mois} className="border-t border-zinc-800/60">
                    <td className="py-1.5 text-zinc-200">{m.libelle}</td>
                    <td className="text-zinc-400">{m.count}</td>
                    <td className="text-zinc-400">{money(m.sous_total)}</td>
                    <td className="text-green-400/80">{money(m.tps)}</td>
                    <td className="text-green-400/80">{money(m.tvq)}</td>
                    <td className="text-right text-zinc-100">{money(m.total_ttc)}</td>
                  </tr>
                ))}
              </tbody>
              {sales?.totaux?.count > 0 && (
                <tfoot><tr className="border-t border-amber-500/30 font-semibold">
                  <td className="py-2 text-amber-200">TOTAL {year}</td>
                  <td className="text-amber-200">{sales.totaux.count}</td>
                  <td className="text-amber-200">{money(sales.totaux.sous_total)}</td>
                  <td className="text-green-300">{money(sales.totaux.tps)}</td>
                  <td className="text-green-300">{money(sales.totaux.tvq)}</td>
                  <td className="text-right text-amber-200" data-testid="kz-sales-total">{money(sales.totaux.total_ttc)}</td>
                </tr></tfoot>
              )}
            </table>
          </div>

          {/* Rapport par entité (Assurances, Intérêts inclus en catégories) */}
          <div className="space-y-4">
            {report && Object.entries(report.by_entity).map(([ent, b]) => {
              if (!b.count) return null;
              const meta = ENTITY_META[ent] || { label: ent === "non_classe" ? "Non classé" : ent, color: "#71717a", icon: Buildings };
              return (
                <div key={ent} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`kz-fiscal-${ent}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: meta.color }}>
                      <meta.icon size={16} /> {meta.label}
                    </h3>
                    <span className="text-sm text-zinc-300">{money(b.total)} · {b.count} reçus</span>
                  </div>
                  <table className="w-full text-xs">
                    <thead><tr className="text-zinc-500 text-left">
                      <th className="py-1">Catégorie</th><th>Nb</th><th>Sous-total</th><th>TPS</th><th>TVQ</th><th className="text-right">Total</th>
                    </tr></thead>
                    <tbody>
                      {Object.entries(b.by_category).map(([cat, c]) => (
                        <tr key={cat} className="border-t border-zinc-800/60">
                          <td className="py-1.5 text-zinc-200">{cat}</td>
                          <td className="text-zinc-400">{c.count}</td>
                          <td className="text-zinc-400">{money(c.subtotal)}</td>
                          <td className="text-green-400/80">{money(c.tps)}</td>
                          <td className="text-green-400/80">{money(c.tvq)}</td>
                          <td className="text-right text-zinc-100">{money(c.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Rapprochement bancaire ---------------- */
function RapprochementBancaire() {
  const [csvText, setCsvText] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/kz-compta/bank/transactions").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const importCsv = async () => {
    if (!csvText.trim()) { toast.error("Colle ou choisis un fichier CSV"); return; }
    setBusy(true);
    try {
      const r = await api.post("/kz-compta/bank/import", { csv_text: csvText, replace: true });
      toast.success(`${r.data.imported} ligne(s) importée(s)`);
      const rec = await api.post("/kz-compta/bank/reconcile");
      toast.success(`${rec.data.matched} appariée(s) automatiquement`);
      setCsvText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'import"); }
    setBusy(false);
  };

  const clearAll = async () => {
    setBusy(true);
    try { await api.delete("/kz-compta/bank/transactions"); toast.success("Relevé effacé"); load(); }
    catch { toast.error("Erreur"); }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="kz-banque">
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
        <h3 className="text-sm font-bold text-zinc-200 mb-1">Importer un relevé bancaire</h3>
        <p className="text-xs text-zinc-500 mb-3">Format CSV : <code>Date, Description, Montant</code> (ex: 2025-09-16, RONA, 124.50). Les lignes sont appariées automatiquement à tes reçus (même montant, date ± 5 jours).</p>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="kz-bank-file"
            className="text-xs text-zinc-300 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-zinc-800 file:text-zinc-200" />
          <span className="text-xs text-zinc-600">ou colle ci-dessous</span>
        </div>
        <textarea data-testid="kz-bank-csv" value={csvText} onChange={(e) => setCsvText(e.target.value)}
          placeholder={"Date,Description,Montant\n2025-09-16,RONA VAL-DOR,124.50"}
          className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-2 text-xs text-zinc-200 font-mono" rows={4} />
        <div className="flex gap-2 mt-3">
          <button data-testid="kz-bank-import" onClick={importCsv} disabled={busy}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-zinc-950 disabled:opacity-50" style={{ background: GOLD }}>
            <ArrowsLeftRight size={16} /> Importer & apparier
          </button>
          {data?.count > 0 && (
            <button data-testid="kz-bank-clear" onClick={clearAll} disabled={busy}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-zinc-800 text-zinc-300 hover:bg-zinc-700">
              <Trash size={16} /> Effacer
            </button>
          )}
        </div>
      </div>

      {data?.count > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card label="Lignes" value={data.count} color="#fff" />
            <Card label="Appariées" value={data.matched_count} color="#22c55e" test="kz-bank-matched" />
            <Card label="Non appariées" value={data.unmatched_count} color="#f59e0b" test="kz-bank-unmatched" />
            <Card label="Montant non apparié" value={money(data.unmatched_total)} color="#f59e0b" />
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="text-zinc-500 text-left bg-zinc-950/50">
                <th className="py-2 px-3">Date</th><th>Description</th><th>Montant</th><th>État</th>
              </tr></thead>
              <tbody>
                {data.transactions.map((t) => (
                  <tr key={t.id} className="border-t border-zinc-800/60" data-testid={`kz-bank-row-${t.id}`}>
                    <td className="py-2 px-3 text-zinc-400">{t.date}</td>
                    <td className="text-zinc-200">{t.description}</td>
                    <td className="text-zinc-300">{money(t.amount)}</td>
                    <td>
                      {t.matched
                        ? <span className="text-green-400 flex items-center gap-1"><CheckCircle size={14} /> {t.matched_merchant || "Apparié"}</span>
                        : <span className="text-amber-400 flex items-center gap-1"><Warning size={14} /> À vérifier</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Card({ label, value, color, test }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid={test}>
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="text-xl font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
